import * as React from 'react';
import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import { Button, ButtonProps } from '@/components/ui/button';

// Shared "call-to-action" button. Thin wrapper over the shadcn Button
// primitive that bakes in the canonical CTA treatment: label + trailing
// ChevronRight icon. Use it for forward-motion actions ("Rate this
// resort", "Load more reviews", "View all", "Write a review", etc.) so
// the icon + spacing + size stay consistent across surfaces.
//
// Pass `href` to render as a Next/Link (uses Button's `asChild`).
// Pass `onClick` (without href) to render as a regular <button>.
// Override `iconRight` to swap the icon, or set it to `null` to hide.
// `iconLeft` is optional — useful for "Back" / "Write" style affordances.
//
// All other shadcn Button props (variant, size, disabled, className...)
// pass through.
//
// Examples:
//   <CtaButton href={`#${REVIEW_ANCHOR}`}>
//     {t('resort.reviews.rateThisResortTitle')}
//   </CtaButton>
//
//   <CtaButton variant="outline" onClick={loadNext} disabled={isLoading}>
//     {t('common.loadMore')}
//   </CtaButton>
//
//   <CtaButton iconLeft={<PenLine />} href="/write">Write a review</CtaButton>

type CtaButtonOwnProps = {
  /** Renders as a Next/Link when provided. Mutually exclusive with onClick. */
  href?: string;
  /** Forwarded to Next/Link when `href` is set. */
  scroll?: boolean;
  /** Trailing icon. Defaults to ChevronRight. Pass `null` to omit. */
  iconRight?: React.ReactNode | null;
  /** Optional leading icon. */
  iconLeft?: React.ReactNode;
};

export type CtaButtonProps = CtaButtonOwnProps & Omit<ButtonProps, 'asChild'>;

export const CtaButton = ({
  href,
  scroll,
  iconRight = <ChevronRight aria-hidden />,
  iconLeft,
  children,
  ...buttonProps
}: CtaButtonProps) => {
  const inner = (
    <>
      {iconLeft}
      {children}
      {iconRight}
    </>
  );

  if (href) {
    return (
      <Button asChild {...buttonProps}>
        <Link href={href} scroll={scroll}>{inner}</Link>
      </Button>
    );
  }

  return (
    <Button type={buttonProps.type ?? 'button'} {...buttonProps}>
      {inner}
    </Button>
  );
};
